import React from 'react';
import { Shield, Users, Lightbulb, GraduationCap } from 'lucide-react';
import { summary } from '../data/mock';
import useScrollAnimation from '../hooks/useScrollAnimation';

const About = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [contentRef, contentVisible] = useScrollAnimation(0.1);
  const [statsRef, statsVisible] = useScrollAnimation(0.2);
  const [highlightsRef, highlightsVisible] = useScrollAnimation(0.1);

  const highlights = [
    {
      icon: Shield,
      title: 'Cybersecurity Expert',
      description: 'Led incident response for phishing and ransomware events with transparency and risk containment'
    },
    {
      icon: Users,
      title: 'Strategic Leader',
      description: 'Partner with executives and boards to align technology with institutional mission'
    },
    {
      icon: Lightbulb,
      title: 'Innovation Driver',
      description: 'Evaluate and integrate emerging technologies including responsible AI in education'
    },
    {
      icon: GraduationCap,
      title: 'Educator at Heart',
      description: 'Active Computer Science teacher, bridging leadership with direct student impact'
    }
  ];

  return (
    <section id="about" className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - Summary */}
          <div>
            <div 
              ref={headerRef}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 mb-6 animate-on-scroll ${headerVisible ? 'visible' : ''}`}
            >
              <span className="text-teal-400 text-sm font-medium">About Me</span>
            </div>
            
            <h2 
              ref={contentRef}
              className={`text-3xl md:text-4xl font-bold text-white mb-6 animate-on-scroll ${contentVisible ? 'visible' : ''}`}
            >
              Bridging Technology & Education
            </h2>
            
            <div className={`space-y-4 animate-on-scroll stagger-1 ${contentVisible ? 'visible' : ''}`}>
              {summary.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-slate-400 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Stats */}
            <div 
              ref={statsRef}
              className="grid grid-cols-3 gap-6 mt-10"
            >
              <div className={`text-center p-4 rounded-xl bg-slate-900 border border-slate-800 animate-scale-in stagger-1 ${statsVisible ? 'visible' : ''}`}>
                <div className="text-3xl font-bold text-teal-400 mb-1">9+</div>
                <div className="text-slate-500 text-sm">Years Experience</div>
              </div>
              <div className={`text-center p-4 rounded-xl bg-slate-900 border border-slate-800 animate-scale-in stagger-2 ${statsVisible ? 'visible' : ''}`}>
                <div className="text-3xl font-bold text-teal-400 mb-1">$300K</div>
                <div className="text-slate-500 text-sm">Budget Managed</div>
              </div>
              <div className={`text-center p-4 rounded-xl bg-slate-900 border border-slate-800 animate-scale-in stagger-3 ${statsVisible ? 'visible' : ''}`}>
                <div className="text-3xl font-bold text-teal-400 mb-1">2</div>
                <div className="text-slate-500 text-sm">Campuses</div>
              </div>
            </div>
          </div>

          {/* Right Column - Highlights */}
          <div ref={highlightsRef} className="space-y-6">
            {highlights.map((item, index) => (
              <div
                key={index}
                className={`group p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-teal-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/5 animate-slide-right stagger-${index + 1} ${highlightsVisible ? 'visible' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-teal-500/10 text-teal-400 group-hover:bg-teal-500/20 transition-colors">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-teal-400 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
